import { Alert, Box } from '@mui/material';
import React, { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import { Loader } from '@common/ui/Loader';

import AuthContext from './authContext';
import { AppType, AuthData, IframeAuth, IframeMessage, MessageType, User } from './interfaces';
import { showAdminPortal, onSetUpMfa, onEnterMfa, switchTenant, isInIframe } from './common';

const useIframeMessage = () => {
  const sendMessage = useCallback((message: IframeMessage) => {
    window.parent.postMessage(message, '*');
  }, []);

  return { sendMessage };
};

const useAuthListener = (onAuth: (auth: IframeAuth) => void) => {
  const handleMessage = useCallback(
    (event: MessageEvent<IframeMessage>) => {
      if (event.data.type === MessageType.AUTHENTICATE && event.data.auth) {
        onAuth(event.data.auth);
      }
    },
    [onAuth],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);
};

const useAuthData = (auth: IframeAuth | null): AuthData | null => {
  return useMemo(() => {
    if (!auth) return null;

    const user: User = {
      id: '',
      email: '',
      name: '',
      app: AppType.Backstage,
      tenantId: '',
    };

    return {
      user,
      accessToken: auth.token,
      tenants: [],
      isMfaPassed: false,
      isMfaEnrolled: false,
    };
  }, [auth]);
};

// Main component
export function BackstageAuthenticator({ children }: PropsWithChildren) {
  const [auth, setAuth] = React.useState<IframeAuth | null>(null);
  const { sendMessage } = useIframeMessage();
  useAuthListener(setAuth);
  const authData = useAuthData(auth);

  useEffect(() => {
    sendMessage({ type: MessageType.READY });
  }, [sendMessage]);

  if (!authData) {
    return <Loader absolute />;
  }

  const contextValue = {
    ...authData,
    showAdminPortal,
    onSetUpMfa,
    onEnterMfa,
    switchTenant,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function BackstageAuthProvider({ children }: PropsWithChildren) {
  if (!isInIframe()) {
    // eslint-disable-next-line no-console
    console.error('App is not running in an iframe');
    return (
      <Box p={2}>
        <Alert severity="error">App is not running in an iframe</Alert>
      </Box>
    );
  }

  return <BackstageAuthenticator>{children}</BackstageAuthenticator>;
}
